import { useTheme } from '@emotion/react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DateTime } from 'luxon';
import { useEffect, useMemo, useRef } from 'react';
import {
  darkTheme,
  lightTheme,
} from '../../../../components/highcharts/themes';
import InfoTooltipIcon from '@/components/icons/InfoTooltipIcon';
import { useTranslation } from 'react-i18next';

function getTypeColor(type) {
  switch (type) {
    case 'Vindmøller':
      return 'rgba(0, 169, 143, 0.7)';
    case 'Vandkraft':
      return 'rgba(10, 81, 93, 0.7)';
    case 'Olie':
      return 'rgba(51, 51, 51, 0.7)';
    case 'Kul':
      return 'rgba(51, 51, 51, 0.7)';
    case 'Biomasse':
      return 'rgba(0, 104, 44, 0.7)';
    case 'Naturgas':
      return 'rgba(160, 193, 194, 0.7)';
    case 'Solceller':
      return 'rgba(255, 212, 36, 0.7)';
    case 'Affald':
      return 'rgba(252, 186, 3, 0.7)';
    case 'Atomkraft':
      return 'rgba(255, 102, 0, 0.7)';
    case 'Anden VE':
      return 'rgba(20, 208, 36, 0.7)';
    case 'Ukendt':
      return 'rgba(135, 135, 135, 0.7)';
    default:
      return 'rgba(85, 85, 85, 0.7)';
  }
}

const EsgDistributionStackedLineChart = ({ GridMixData }) => {
  const theme = useTheme();
  const chartComponentRef = useRef(null);
  const { t } = useTranslation('energyxplorer-esg');

  // Transform GridMixData into Highcharts series format
  const seriesData = useMemo(() => {
    if (GridMixData[0] === null || GridMixData[0] === undefined) return [];
    const categories = Object.keys(GridMixData[0]).filter(
      (key) => key !== 'timestamp',
    );
    const series = categories.map((category) => ({
      name: category,
      data: GridMixData.map((item) => ({
        x: DateTime.fromISO(item.timestamp).toMillis(), // Convert ISO string to milliseconds
        y: item[category],
      })),
      color: getTypeColor(category), // Explicitly set the color for the series
    }));
    return series;
  }, [GridMixData]);

  const options = {
    chart: {
      type: 'area', // Change to area chart
      height: 400,
      zooming: { type: 'x', mouseWheel: { enabled: false } },
    },
    title: {
      text: '',
    },
    subtitle: {
      text: '',
    },
    accessibility: {
      point: {
        valueDescriptionFormat:
          '{index}. {point.category}, {point.y:.1f}%, {point.percentage:.1f}%.',
      },
    },
    xAxis: {
      type: 'datetime',
      labels: {
        formatter: function () {
          const millis = DateTime.fromMillis(this.value);
          if (this.dateTimeLabelFormat.includes('e') || this.isFirst) {
            return millis.setLocale('da-DK').toLocaleString({
              month: 'short',
              day: 'numeric',
              timeZone: 'Europe/Copenhagen',
            });
          } else if (this.dateTimeLabelFormat.includes('y') || this.isFirst) {
            return millis.setLocale('da-DK').toLocaleString({
              month: 'short',
              year: '2-digit',
              timeZone: 'Europe/Copenhagen',
            });
          } else
            return millis.setLocale('da-DK').toLocaleString({
              hour: '2-digit',
              minute: '2-digit',
              timeZone: 'Europe/Copenhagen',
            });
        },
      },
    },
    yAxis: {
      labels: {
        format: '{value}%', // Display percentage on the y-axis
      },
      title: {
        // Disable y-axis title
      },
      min: 0,
      max: 100,
    },
    tooltip: {
      formatter: function () {
        const millis = DateTime.fromMillis(this.x);
        const date = millis.setLocale('da-DK').toLocaleString({
          month: 'short',
          day: 'numeric',
          year: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'Europe/Copenhagen',
        });
        let tooltip = `<span>${date}</span><br/>`;
        this.points?.forEach((point) => {
          tooltip += `<span style="color:${point.color}">\u25CF</span> ${
            point.series.name
          }: <b>${point.percentage.toFixed(1)}%</b><br/>`;
        });
        return tooltip;
      },
      shared: true,
    },
    plotOptions: {
      series: {
        stacking: 'percent', // Enable percentage stacking
        marker: {
          enabled: false,
        },
      },
      area: {
        stacking: 'percent', // Ensure stacking for area chart
        marker: {
          enabled: false,
        },
      },
    },
    series: seriesData,
    legend: {
      enabled: false,
    },
  };

  useEffect(() => {
    const themeOptions = theme.palette.mode === 'dark' ? darkTheme : lightTheme;

    if (chartComponentRef.current?.chart) {
      const chart = chartComponentRef.current.chart;
      chart.update(
        {
          chart: { backgroundColor: themeOptions.chart.backgroundColor },
          time: {
            // timezoneOffset: new Date().getTimezoneOffset('da-DK'),
            useUTC: true,
          },
          title: { style: { color: themeOptions.title.style.color } },
          xAxis: {
            gridLineColor: 'transparent',
            lineColor: themeOptions.xAxis.lineColor,
            labels: { style: { color: themeOptions.xAxis.labels.style.color } },
          },
          yAxis: [
            {
              gridLineColor: 'transparent',
              labels: {
                style: { color: themeOptions.yAxis.labels.style.color },
              },
            },
          ],
          legend: {
            itemStyle: { color: themeOptions.legend.itemStyle.color },
          },
          tooltip: {
            backgroundColor: themeOptions.tooltip.backgroundColor,
            borderColor: themeOptions.tooltip.borderColor,
            style: {
              color: theme.palette.mode === 'dark' ? 'white' : 'black', // ✅ Dynamic text color
            },
          },
        },
        false,
      );
      chart.redraw();
    }
  }, [theme]);

  return (
    <Card variant="outlined" sx={{ p: 2, borderRadius: 2, minHeight: 240 }}>
      <CardContent>
        <Stack
          direction="column"
          color="inherit"
          component={Card}
          elevation={0}
          sx={{
            transition: 'box-shadow 0.3s ease',
            height: '100%',
            background: 'transparent',
          }}
        >
          <Stack spacing={1} alignItems="center" justifyContent="center">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    fontWeight="bold"
                  >
                    {t('energy_distribution_stacked_chart.label')}
                  </Typography>
                  <InfoTooltipIcon
                    title={t('energy_distribution_stacked_chart.description')}
                  />
                </Box>
              </Box>
            </Box>
            <Box display="flex" justifyContent="center" width="100%">
              <HighchartsReact
                highcharts={Highcharts}
                options={options}
                containerProps={{ style: { width: '100%' } }}
                ref={chartComponentRef}
              />
            </Box>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default EsgDistributionStackedLineChart;
